import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActiveRounded";
import WarningAmberIcon from "@mui/icons-material/WarningAmberRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getProfile } from "../../services/user.service";
import {
  buildAlertsFromProfile,
  dismissAlertId,
  getDismissedAlertIds,
  undismissAllAlerts
} from "../../utils/healthAlerts";
import "./Alerts.scss";


const ICONS = {
  danger: ErrorOutlineIcon,
  warning: WarningAmberIcon,
  info: InfoOutlinedIcon
};

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      setError("");
      setLoading(true);
      const profile = await getProfile();
      const dismissed = getDismissedAlertIds();
      setAlerts(buildAlertsFromProfile(profile, dismissed));
    } catch (e) {
      setError(e?.response?.data?.message || "Could not load alerts.");
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const dismiss = (id) => {
    dismissAlertId(id);
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  };

  const clearDismissed = () => {
    undismissAllAlerts();
    refresh();
  };

  return (
    <div className="alerts-page">
      <header className="alerts-page__header">
        <div className="alerts-page__title-row">
          <div className="alerts-page__icon-wrap">
            <NotificationsActiveIcon />
          </div>
          <div>
            <h1>Health alerts</h1>
            <p>
              Priority items derived from your latest uploaded labs. Dismissing hides an item on this device only — it does
              not change your stored reports.
            </p>
          </div>
        </div>
        <div className="alerts-page__actions">
          <Link to="/upload" className="alerts-page__link-btn">
            Upload report
          </Link>
          <button type="button" className="alerts-page__ghost-btn" onClick={refresh} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </header>

      {error ? <p className="alerts-page__error">{error}</p> : null}

      {loading ? (
        <div className="alerts-page__skeleton" aria-hidden>
          {[1, 2, 3].map((k) => (
            <div key={k} className="alerts-page__skeleton-row" />
          ))}
        </div>
      ) : (
        <div className="alerts-page__list-wrap">
          <div className="alerts-page__list-head">
            <span className="alerts-page__count">
              {alerts.length} active alert{alerts.length !== 1 ? "s" : ""}
            </span>
            <button type="button" className="alerts-page__text-btn" onClick={clearDismissed}>
              Restore dismissed
            </button>
          </div>

          <AnimatePresence mode="popLayout">
            {alerts.map((alert) => {
              const Icon = ICONS[alert.type] || InfoOutlinedIcon;
              return (
                <motion.article
                  key={alert.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                  transition={{ duration: 0.22 }}
                  className={`alert-card alert-card--${alert.type}`}
                >
                  <div className={`alert-card__icon alert-card__icon--${alert.type}`}>
                    <Icon />
                  </div>
                  <div className="alert-card__body">
                    <div className="alert-card__top">
                      <h2>{alert.title}</h2>
                      <button
                        type="button"
                        className="alert-card__dismiss"
                        onClick={() => dismiss(alert.id)}
                        aria-label="Dismiss alert"
                      >
                        <CloseIcon />
                      </button>
                    </div>
                    <p>{alert.description}</p>
                    <span className="alert-card__time">{alert.time}</span>
                  </div>
                </motion.article>
              );
            })}
          </AnimatePresence>

          {alerts.length === 0 && !loading && (
            <motion.div
              className="alerts-page__empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <NotificationsActiveIcon className="alerts-page__empty-icon" />
              <h3>All clear</h3>
              <p>No active alerts right now. Upload a new lab PDF to refresh this list.</p>
              <Link to="/upload" className="alerts-page__link-btn alerts-page__link-btn--inline">
                Go to upload
              </Link>
            </motion.div>
          )}
        </div>
      )}
    </div>
  );
}
